import { styled } from '@mui/material/styles';
import Avatar from 'components/common/avatar/Avatar';
import AddPostModal from 'components/modals/addPostModal/AddPostModal';
import { USER_PROFILE } from 'constants/routes';
import useUserData from 'hooks/useUserData';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArticleSourceType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { useLocation } from 'react-router-dom';
import QuestionFillIcon from 'remixicon-react/QuestionFillIcon';
import { theme } from 'config/theme';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import FileListFillIcon from 'remixicon-react/FileListFillIcon';
import ChatQuoteFillIcon from 'remixicon-react/ChatQuoteFillIcon';
import { UserLink } from '@buildwise/pages/knowledge/components/DraftStatusMessage';

type LongAddPostButtonProps = {
  disabled?: boolean;
  formId?: string;
  onAddArticleCallback?: (options: { article: ArticleType }) => void;
};

function LongAddPostButton({
  disabled = false,
  formId,
  onAddArticleCallback,
}: LongAddPostButtonProps) {
  const { state } = useLocation();
  const { user } = useUserData();
  const [openAddArticleModal, setOpenAddArticleModal] = useState<ArticleSourceType | undefined>(
    !!state?.articleFormState ? ArticleSourceType.POST : undefined,
  );
  const { isLoadingBuildWiseModules, homeModuleFeatures } = useBuildWiseModules();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const showUserAvatar = useMemo(
    () =>
      !(
        homeModuleFeatures['Ask Peers'].isActive &&
        homeModuleFeatures['Suggest Improvements'].isActive
      ),
    [homeModuleFeatures],
  );

  useLayoutEffect(() => {
    if (containerRef.current) {
      if (containerRef.current.clientWidth < containerRef.current.scrollWidth) {
        containerRef.current.style.display = 'block';
        if (containerRef.current.firstElementChild) {
          (containerRef.current.firstElementChild as HTMLDivElement).style.marginBottom = '12px';
        }
      }
    }
  }, [containerRef.current, isLoadingBuildWiseModules, homeModuleFeatures]);

  if (isLoadingBuildWiseModules) {
    return null;
  }

  return (
    <>
      <ButtonsContainer
        ref={containerRef}
        justifyContent={showUserAvatar ? 'space-between' : 'center'}
      >
        {showUserAvatar && (
          <Box display="flex" alignItems="center">
            <Avatar
              src={user?.profileImageURL}
              size={38}
              name={user?.userNameToDisplay}
              to={USER_PROFILE.replace(':userName', user?.username || '')}
            />
            <Box ml={1}>
              <UserLink
                mail={user?.username || ''}
                name={user?.userNameToDisplay || ''}
                sx={{
                  lineHeight: 1.2,
                  display: 'block',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  width: 'fit-content',
                }}
              />
              <Typography variant="caption" title="Job Title" whiteSpace="nowrap">
                {user?.title || ''}
              </Typography>
            </Box>
          </Box>
        )}

        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <Button
            disabled={disabled}
            startIcon={<FileListFillIcon size={20} color={theme.palette.custom.pirateGold} />}
            onClick={() => setOpenAddArticleModal(ArticleSourceType.POST)}
            variant="contained"
            sx={(theme) => ({
              color: theme.palette.custom.dusk,
              fontSize: 15,
              whiteSpace: 'nowrap',
              fontWeight: 700,
              backgroundColor: theme.palette.custom.yellowBorder,
              '&:hover': {
                backgroundColor: theme.palette.custom.yellowBorder,
              },
            })}
          >
            {homeModuleFeatures['Share Knowledge'].displayName}
          </Button>
          {homeModuleFeatures['Ask Peers'].isActive && (
            <Button
              disabled={disabled}
              startIcon={<QuestionFillIcon size={20} color={theme.palette.custom.dodgerBlue} />}
              onClick={() => setOpenAddArticleModal(ArticleSourceType.QUESTION)}
              variant="contained"
              sx={{
                color: theme.palette.custom.dusk,
                border: 'none',
                fontSize: 15,
                whiteSpace: 'nowrap',
                fontWeight: 700,
                backgroundColor: '#c7ecff',
                '&:hover': {
                  border: 'none',
                  backgroundColor: '#c7ecff',
                },
              }}
            >
              {homeModuleFeatures['Ask Peers'].displayName}
            </Button>
          )}
          {homeModuleFeatures['Suggest Improvements'].isActive && (
            <Button
              disabled={disabled}
              startIcon={<ChatQuoteFillIcon size={20} color={theme.palette.custom.cloverGreen} />}
              onClick={() => setOpenAddArticleModal(ArticleSourceType.SUGGESTION)}
              variant="contained"
              sx={{
                color: theme.palette.custom.dusk,
                fontSize: 15,
                whiteSpace: 'nowrap',
                fontWeight: 700,
                border: 'none',
                backgroundColor: '#d2ffde',
                '&:hover': {
                  border: 'none',
                  backgroundColor: '#d2ffde',
                },
              }}
            >
              {homeModuleFeatures['Suggest Improvements'].displayName}
            </Button>
          )}
        </Box>
      </ButtonsContainer>

      {openAddArticleModal && (
        <AddPostModal
          articleType={openAddArticleModal}
          open={!!openAddArticleModal}
          setOpen={() => setOpenAddArticleModal(undefined)}
          selectedFeedFilters={[]}
          {...(onAddArticleCallback && { onAddArticleCallback })}
          {...(formId && { formIds: [formId] })}
          articleFormState={state?.articleFormState}
        />
      )}
    </>
  );
}

const ButtonsContainer = styled(Box, { label: 'ButtonsContainer' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  backgroundColor: theme.palette.custom.white,
  padding: '12px 16px',
  borderRadius: 8,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  overflow: 'hidden',
}));

export default LongAddPostButton;
